/**
 * URL-state hook — keeps a stringly-typed object in sync with
 * `?key=value` query params on `window.location` so dashboard views are
 * shareable. Avoids router dependency.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

type Stringy = Record<string, string | undefined>;

function read(): Stringy {
  if (typeof window === "undefined") return {};
  const out: Stringy = {};
  const params = new URLSearchParams(window.location.search);
  for (const [k, v] of params.entries()) out[k] = v;
  return out;
}

const SUBSCRIBERS = new Set<() => void>();

function notify() {
  for (const cb of SUBSCRIBERS) cb();
}

if (typeof window !== "undefined") {
  window.addEventListener("popstate", notify);
}

function write(next: Stringy, replace = false) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(next)) {
    if (v == null || v === "") continue;
    params.set(k, v);
  }
  const url = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}${window.location.hash}`;
  if (replace) window.history.replaceState({}, "", url);
  else window.history.pushState({}, "", url);
  notify();
}

export function useUrlState(): [Stringy, (patch: Stringy, opts?: { replace?: boolean }) => void] {
  const [state, setState] = useState<Stringy>(() => read());

  useEffect(() => {
    const cb = () => setState(read());
    SUBSCRIBERS.add(cb);
    return () => { SUBSCRIBERS.delete(cb); };
  }, []);

  const patch = useCallback((p: Stringy, opts?: { replace?: boolean }) => {
    const next = { ...read(), ...p };
    for (const [k, v] of Object.entries(p)) {
      if (v == null || v === "") delete next[k];
    }
    write(next, opts?.replace ?? false);
  }, []);

  return [state, patch];
}

/** Convenience getter for a single key. */
export function useUrlValue(key: string): [string | undefined, (v: string | undefined) => void] {
  const [state, patch] = useUrlState();
  const value = state[key];
  const set = useCallback((v: string | undefined) => patch({ [key]: v }), [patch, key]);
  return [value, set];
}

/** Build a filter object from the URL using a key whitelist. */
export function useFiltersFromUrl<T extends Stringy>(keys: readonly (keyof T & string)[]): T {
  const [state] = useUrlState();
  return useMemo(() => {
    const out = {} as T;
    for (const k of keys) {
      const v = state[k];
      if (v != null) (out as Stringy)[k] = v;
    }
    return out;
  }, [state, keys]);
}
